import { FC, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Alert,
	AlertDescription,
	AlertIcon,
	ArrowLeftOutline,
	Box,
	Button,
	IconButton,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Text,
} from '@bonusly/design-system';
import { FeatureFlag, useAnalytics } from '@bonusly/who-am-i';

import { Reveal } from '../../../../../../components/display/reveal';
import { Award } from '../../../../../../types/bonus';
import banner from '../../../../assets/images/award-banner.svg';
import { AwardSelectionList } from './AwardSelectionList';
import { EditableAwardInput } from './EditableAwardInput';
import { useAwardModal } from './hooks/useAwardModal';
import { useGiveableAwards } from './hooks/useGiveableAwards';
import { OldEditableAwardInput } from './OldEditableAwardInput';
import { OldUneditableAwardInput } from './OldUneditableAwardInput';
import { UneditableAwardInput } from './UneditableAwardInput';

export const GiveAwardModal: FC = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { t } = useTranslation(['common', 'recognition']);
	const { track } = useAnalytics();
	const modal = useAwardModal();

	const [state, dispatch] = useReducer(reducer, initialState, (state) => ({
		...state,
		group: Math.random().toString(36).substring(7),
	}));

	const { group, hideGiver, isMissingSelection, selectedAward, stage } = state;
	const isTemplatedAward = !selectedAward?.reason_editable;

	const { awards } = useGiveableAwards();

	const resetModal = () => dispatch({ type: ActionType.Reset });
	const goToSelectAward = () => dispatch({ type: ActionType.SetStage, stage: Stage.Select });
	const selectAward = (award: Award) => dispatch({ type: ActionType.SelectAward, award });
	const setHideGiver = (hideGiver: boolean) => dispatch({ type: ActionType.SetHideGiver, hideGiver });
	const goToGiveAward = () =>
		dispatch({
			type: state.selectedAward ? ActionType.ViewAward : ActionType.DisplayNoAward,
		});

	const trackGoToSelectAward = () => {
		track('Award Back Button', {
			award_name: selectedAward?.giver.first_name,
			award_type: isTemplatedAward ? 'Templated' : 'Non Templated',
			budget_timeframe: selectedAward?.budget_period,
		});
		goToSelectAward();
	};

	return (
		<Modal scrollBehavior="inside" size="2xl" closeOnOverlayClick={!isSubmitting} onCloseComplete={resetModal} {...modal}>
			<ModalOverlay />
			<ModalContent data-testid="give-award-modal">
				<Reveal if={stage == Stage.Select}>
					<ModalHeader
						alignItems="center"
						borderBottomWidth={rem(1)}
						borderColor="gray.100"
						borderStyle="solid"
						color="gray.900"
						display="flex"
						justifyContent="space-between"
						px={5}
						py={4}
					>
						<Text as="span" color="gray.900" fontSize="lg" fontWeight="semibold" lineHeight={8}>
							{t('awards.give_modal.select_award_header', { ns: 'recognition' })}
						</Text>
						<ModalCloseButton
							bg="blackAlpha.50"
							borderRadius="full"
							data-testid="modal-close-button"
							h={rem(36)}
							onClick={() => track('Award Give Cancel', { screen: 'select_award' })}
							pos="static"
							w={rem(36)}
							_hover={{ bg: 'blackAlpha.100' }}
						/>
					</ModalHeader>

					<ModalBody p={0}>
						<Image alt={t('recognition:awards.give_modal.select_award_banner') ?? ''} src={banner} />
						<Reveal if={isMissingSelection}>
							<Box px={5} py={4}>
								<Alert status="error">
									<AlertIcon color="red.700" />
									<AlertDescription color="red.700" fontSize="md">
										{t('recognition:awards.give_modal.error_no_award_selected')}
									</AlertDescription>
								</Alert>
							</Box>
						</Reveal>

						<AwardSelectionList awards={awards} group={group} selectAward={selectAward} selectedAward={selectedAward} isTemplatedAward={isTemplatedAward} />
					</ModalBody>

					<ModalFooter>
						<Button onClick={goToGiveAward} size="lg">
							{t('next', { ns: 'common' })}
						</Button>
					</ModalFooter>
				</Reveal>

				<Reveal if={stage === Stage.Give}>
					<ModalHeader
						alignItems="center"
						borderBottomWidth={rem(1)}
						borderColor="gray.100"
						borderStyle="solid"
						color="gray.900"
						display="flex"
						justifyContent="space-between"
						px={5}
						py={4}
					>
						<Box maxH={rem(36)}>
							<IconButton
								aria-label={t('go_back', { ns: 'common' })}
								bg="blackAlpha.50"
								borderRadius="full"
								data-testid="modal-back-button"
								height={rem(36)}
								minH="auto"
								minW="auto"
								onClick={trackGoToSelectAward}
								w={rem(36)}
								_hover={{ bg: 'blackAlpha.100' }}
							>
								<ArrowLeftOutline color="gray.600" size="sm" />
							</IconButton>

							<Text as="span" color="gray.900" fontSize="lg" fontWeight="semibold" lineHeight={8} ml={2.5}>
								{t('awards.give_modal.give_award_header', { ns: 'recognition' })}
							</Text>
						</Box>
						<ModalCloseButton
							bg="blackAlpha.50"
							borderRadius="full"
							data-testid="modal-close-button"
							h={rem(36)}
							pos="static"
							w={rem(36)}
							onClick={() => track('Award Give Cancel', { screen: 'give_award' })}
							_hover={{ bg: 'blackAlpha.100' }}
						/>
					</ModalHeader>

					<FeatureFlag
						flag="awards-redesign"
						fallback={
							<>
								{isTemplatedAward ? (
									<OldUneditableAwardInput award={state.selectedAward} hideGiver={hideGiver} setHideGiver={setHideGiver} setIsSubmitting={setIsSubmitting} />
								) : (
									<OldEditableAwardInput award={state.selectedAward} hideGiver={hideGiver} setHideGiver={setHideGiver} setIsSubmitting={setIsSubmitting} />
								)}
							</>
						}
					>
						{isTemplatedAward ? (
							<UneditableAwardInput award={state.selectedAward} hideGiver={hideGiver} setHideGiver={setHideGiver} setIsSubmitting={setIsSubmitting} />
						) : (
							<EditableAwardInput award={state.selectedAward} hideGiver={hideGiver} setHideGiver={setHideGiver} setIsSubmitting={setIsSubmitting} />
						)}
					</FeatureFlag>
				</Reveal>

				{isSubmitting && <Box bgColor="whiteAlpha.700" rounded="md" cursor="not-allowed" inset={0} position="absolute" />}
			</ModalContent>
		</Modal>
	);
};

enum Stage {
	Select,
	Give,
}

enum ActionType {
	DisplayNoAward = 'display-no-award',
	SelectAward = 'select-award',
	SetHideGiver = 'hide-giver',
	SetStage = 'set-stage',
	Reset = 'reset',
	ViewAward = 'view-award',
}

type Action<T extends ActionType, P extends Record<string, unknown> | undefined = undefined> = P extends undefined ? { type: T } : { type: T } & P;
type DisplayNoAwardAction = Action<ActionType.DisplayNoAward>;
type ResetAction = Action<ActionType.Reset>;
type SelectAwardAction = Action<ActionType.SelectAward, { award: Award }>;
type SetHideGiverAction = Action<ActionType.SetHideGiver, { hideGiver: boolean }>;
type SetStageAction = Action<ActionType.SetStage, { stage: Stage }>;
type ViewAwardAction = Action<ActionType.ViewAward>;
type AwardAction = DisplayNoAwardAction | ResetAction | SelectAwardAction | SetHideGiverAction | SetStageAction | ViewAwardAction;

interface AwardState {
	group: string;
	hideGiver: boolean;
	isMissingSelection: boolean;
	selectedAward: Award;
	stage: Stage;
}

const initialState: AwardState = {
	group: '',
	hideGiver: false,
	isMissingSelection: false,
	selectedAward: undefined as unknown as Award,
	stage: Stage.Select,
};

const reducer = (state: AwardState, action: AwardAction): AwardState => {
	switch (action.type) {
		case ActionType.DisplayNoAward:
			return {
				...state,
				isMissingSelection: true,
			};

		case ActionType.Reset:
			return {
				...initialState,
			};

		case ActionType.SelectAward:
			return {
				...state,
				isMissingSelection: false,
				selectedAward: action.award,
			};

		case ActionType.SetStage:
			return {
				...state,
				stage: action.stage,
			};

		case ActionType.ViewAward:
			return {
				...state,
				stage: Stage.Give,
			};

		case ActionType.SetHideGiver:
			return {
				...state,
				hideGiver: action.hideGiver,
			};
	}
};
